import React, { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Card } from "./ui/card";
import { CheckCircle2, ExternalLink, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Pack 3 · Deliverable 3 — Consultant Review.
 *
 * Preview the AI-drafted Executive Growth Report™, add an optional
 * Consultant Perspective™ (max ~1500 chars), and approve it for release
 * to the business leader. Approval triggers async Meeting Brief™ generation.
 */
export default function ReportReviewSheet({ open, lead, onOpenChange, onApproved }) {
  const [loading, setLoading] = useState(false);
  const [lead2, setLead2] = useState(null);
  const [perspective, setPerspective] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !lead?.id) return;
    setLoading(true);
    api.get(`/leads/${lead.id}`)
      .then((r) => {
        const doc = r.data.lead || {};
        setLead2({
          ...doc,
          report_status: r.data.report_status,
          consultant: r.data.consultant,
        });
        setPerspective(doc.consultant_perspective || "");
      })
      .catch(() => toast.error("Failed to load report for review"))
      .finally(() => setLoading(false));
  }, [open, lead?.id]);

  const isApproved = lead2?.report_status === "approved";
  const report = lead2?.report;

  const approve = async () => {
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/approve`, {
        consultant_perspective: perspective.trim(),
      });
      toast.success("Report approved and released to the business leader");
      setLead2({ ...lead2, report_status: "approved", consultant_perspective: perspective.trim() });
      onApproved?.(lead.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally {
      setSaving(false);
    }
  };

  const saveOnly = async () => {
    // Approve + save are the same endpoint; if the report is already approved
    // we still POST to update the perspective.
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/approve`, { consultant_perspective: perspective.trim() });
      toast.success("Consultant Perspective™ saved");
      setLead2({ ...lead2, consultant_perspective: perspective.trim() });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const roadmap = useMemo(() => {
    const rm = report?.roadmap || {};
    const has = ["next_30_days", "next_90_days", "next_180_days"].some((k) => (rm[k] || []).length);
    if (has) return rm;
    const p = report?.priority_improvements || [];
    return {
      next_30_days: p.filter((x) => x.priority === "P0").map((x) => ({ title: x.title, detail: x.rationale })),
      next_90_days: p.filter((x) => x.priority === "P1").map((x) => ({ title: x.title, detail: x.rationale })),
      next_180_days: p.filter((x) => x.priority === "P2").map((x) => ({ title: x.title, detail: x.rationale })),
    };
  }, [report]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="report-review-sheet">
        <SheetHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <SheetTitle className="font-display text-2xl">
                Review · <span className="text-accent">{lead?.company || lead?.name}</span>
              </SheetTitle>
              <SheetDescription>
                {lead?.country} · {lead?.industry} · {lead?.function} · Score {lead?.results?.overall_score ?? "—"}/100
              </SheetDescription>
            </div>
            {isApproved ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest bg-emerald-500/15 border border-emerald-500/40 text-emerald-300" data-testid="review-status-approved">
                <CheckCircle2 className="w-3 h-3" /> Approved
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest bg-amber-500/15 border border-amber-500/40 text-amber-300" data-testid="review-status-pending">
                <ShieldCheck className="w-3 h-3" /> Pending review
              </span>
            )}
          </div>
        </SheetHeader>

        {loading || !lead2 ? (
          <div className="py-20 flex flex-col items-center text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin text-accent" />
            <div className="mt-3 text-sm">Loading report…</div>
          </div>
        ) : (
          <div className="mt-5 space-y-6">
            {/* Executive Summary preview */}
            <PreviewBlock title="Executive Summary" testId="review-exec-summary">
              <p className="text-sm leading-relaxed">{report?.executive_summary || "—"}</p>
            </PreviewBlock>

            {/* Key opportunities */}
            <PreviewBlock title="Key Business Opportunities" testId="review-opportunities">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {(lead2.results?.top_gaps || []).map((g, i) => (
                  <Card key={g.area} className="p-3">
                    <div className="text-[10px] uppercase tracking-widest text-accent">#{i + 1}</div>
                    <div className="font-sans-ui font-semibold text-sm mt-1">{g.area}</div>
                    <div className="text-xs text-muted-foreground mt-1">{g.score}/100</div>
                  </Card>
                ))}
              </div>
            </PreviewBlock>

            {/* Opportunity Analysis */}
            {report?.gap_analysis?.length ? (
              <PreviewBlock title="Opportunity Analysis" testId="review-analysis">
                <ul className="space-y-2">
                  {report.gap_analysis.slice(0, 5).map((g, i) => (
                    <li key={`${g.area}-${i}`} className="text-sm">
                      <span className="font-semibold">{g.area}: </span>
                      <span>{g.finding}</span>
                    </li>
                  ))}
                </ul>
              </PreviewBlock>
            ) : null}

            {/* Roadmap snapshot */}
            <PreviewBlock title="Priority Action Roadmap" testId="review-roadmap">
              <div className="grid grid-cols-3 gap-2 text-xs">
                {["next_30_days", "next_90_days", "next_180_days"].map((k) => (
                  <div key={k} className="rounded-md border border-border/60 p-2">
                    <div className="uppercase tracking-widest text-[10px] text-muted-foreground">
                      {k === "next_30_days" ? "30 days" : k === "next_90_days" ? "90 days" : "180 days"}
                    </div>
                    <div className="mt-1 font-display text-lg">{(roadmap[k] || []).length}</div>
                    <div className="text-[10px] text-muted-foreground">items</div>
                  </div>
                ))}
              </div>
            </PreviewBlock>

            {/* Consultant Perspective editor */}
            <div className="rounded-md border border-accent/40 bg-accent/5 p-4" data-testid="review-perspective">
              <div className="flex items-center gap-2 overline text-accent">
                <Sparkles className="w-3.5 h-3.5" /> Consultant Perspective™ (optional)
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                A short executive note in your voice that appears in Section 7 of the released report. Skip and approve if you prefer to share your view live during the discovery call.
              </p>
              <Textarea
                data-testid="review-perspective-input"
                value={perspective}
                onChange={(e) => setPerspective(e.target.value.slice(0, 1500))}
                placeholder="e.g. Based on your responses, I see three fixable operating levers worth £X in the next 90 days. Let's talk about the first one on our call…"
                className="mt-3 min-h-[140px] text-sm leading-relaxed"
              />
              <div className="text-[10px] text-muted-foreground text-right mt-1">{perspective.length}/1500</div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/60">
              <a href={`/report/${lead.id}`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid="review-open-full">
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open full report
                </Button>
              </a>
              <a href={`${API}/leads/${lead.id}/pdf`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid="review-open-pdf">PDF</Button>
              </a>
              <div className="flex-1" />
              {isApproved ? (
                <Button
                  onClick={saveOnly}
                  disabled={saving}
                  data-testid="review-save-perspective-btn"
                  className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Save perspective
                </Button>
              ) : (
                <Button
                  onClick={approve}
                  disabled={saving}
                  data-testid="review-approve-btn"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Approve &amp; release to business leader
                </Button>
              )}
            </div>

            {!isApproved && (
              <p className="text-xs text-muted-foreground text-center">
                On approval, the AI Meeting Brief™ generates automatically in the background.
              </p>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function PreviewBlock({ title, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="overline">{title}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
